import { observer } from 'mobx-react-lite'
import DetailsSection from '../common/DetailsSection'
import DetailsValue from '../common/DetailsValue'
import { useMemo, useState } from 'react'
import { phoneInputFormat } from 'util/formatters'
import convertSpecialty from 'util/convertSpecialty'
import Modals from 'singletons/Modals'
import Rx from 'models/Provider/visits/PendingVisit/Rxs/Rx'
import useTable from '@ui-components-3/ui/lib/components/Table/hooks/useTable'
import CommonTable from 'components/common/tables/CommonTable/CommonTable'
import { format } from 'date-fns/format'
import PrescriptionDetailsModal from '../common/PrescriptionDetailsModal'
import { orderBy } from 'lodash'

type PrescriptionsSectionProps = {
  // TODO: improve visit type
  visit: any
  editable?: boolean
}

const PrescriptionsSection = ({ visit, editable }: PrescriptionsSectionProps) => {
  const [selectedRx, setSelectedRx] = useState<Rx | null>(null)
  const { isPrescribable, rxs, canAddPrescription, isMemberLocked } = visit || {}

  const data = orderBy([...(rxs?.list || [])], ['writtenDate'], ['desc'])

  const pharmacy = data?.[0]?.pharmacy

  const { address1, city, state, zip, phone, specialty, storeName } = pharmacy || {}

  const columns = useMemo(
    () => [
      {
        id: 'name',
        header: 'Medication',
        accessorKey: 'name',
      },
      {
        id: 'unit',
        header: 'Strength',
        accessorKey: 'unit',
        meta: {
          cellClassName: 'w-[200px]',
        },
      },
      {
        id: 'dispenseUnit',
        header: 'Dispense Unit',
        accessorKey: 'dispenseUnit',
        accessorFn: (row: any) => row?.dispenseUnit?.name,
        meta: {
          cellClassName: 'w-[200px]',
        },
      },
      {
        id: 'quantity',
        header: 'QTY',
        accessorKey: 'quantity',
        meta: {
          cellClassName: 'w-[150px]',
        },
      },
      {
        id: 'refills',
        header: 'Refills',
        accessorKey: 'refills',
        meta: {
          cellClassName: 'w-[150px]',
        },
      },
      {
        id: 'writtenDate',
        header: 'Written Date',
        accessorKey: 'writtenDate',
        accessorFn: (row: any) => {
          const { writtenDate } = row || {}

          return writtenDate ? format(new Date(writtenDate), 'MM/dd/yyyy') : '-'
        },
        meta: {
          cellClassName: 'w-[200px]',
        },
      },
      {
        id: 'actions',
        header: 'Action',
        cell: (cell: any) => (
          <button
            className="text-link-primary text-left whitespace-nowrap"
            type="button"
            onClick={() => setSelectedRx(cell?.row?.original || null)}
          >
            View Details
          </button>
        ),
      },
    ],
    [],
  )

  const table = useTable({
    // using the model with paginationType 'server' without pagination controls and event handlers
    // makes table acting like static table without pagination at all, so we can use all the benefits from
    // useTable hook as well as reuse existing CommonTable component
    paginationType: 'server',
    columns,
    data,
    rowId: 'name',
  })

  const allowEdit = !!editable && !!canAddPrescription

  if (!isPrescribable || (!allowEdit && !data?.length)) {
    return null
  }

  return (
    <DetailsSection title="TimelyCare Prescription">
      {!!pharmacy && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-x-6 gap-y-4">
            <DetailsValue label="Pharmacy" value={storeName} />
            <DetailsValue
              label="Address"
              value={[[address1, city, state, zip].filter(Boolean).join(', '), phoneInputFormat(phone)]
                .filter(Boolean)
                .join('\n')}
            />
            <DetailsValue label="Specialty" value={convertSpecialty(specialty)} />
          </div>
          {!!editable && (
            <button
              type="button"
              className="btn btn-neutral self-start"
              onClick={() => Modals.open('changePharmacy', { visit })}
            >
              Change Pharmacy
            </button>
          )}
          <hr />
        </>
      )}
      {!!data?.length && (
        <div className="max-h-[500px] overflow-y-auto">
          <CommonTable
            aria-label="Prescriptions Table"
            model={table}
            className="w-full typography-body font-medium"
            containerClassName="!min-h-max !basis-auto"
            hideFooter
          />
        </div>
      )}
      {!!allowEdit && (
        <button
          className="btn btn-neutral w-max"
          type="button"
          onClick={() => visit.openAddPrescriptionModal()}
          disabled={isMemberLocked}
        >
          Add Prescription
        </button>
      )}
      {!!selectedRx && (
        <PrescriptionDetailsModal
          rx={selectedRx}
          removeMedication={visit?.rxs?.removeMedication}
          onClose={() => setSelectedRx(null)}
        />
      )}
    </DetailsSection>
  )
}

export default observer(PrescriptionsSection)
